'use strict';

/**
 * Site class contains general purpose site-specific features.
 *
 * @version 1.1.0
 */
class Site {

	/**
	 * Class constructor
	 *
	 * @param {Object} options Options for the class.
	 */
	constructor(options = {}) {
		this.options = {};

		// Init polyfills
		const polyfills = new Polyfills();
		polyfills.init();

		// Init Bartender after mobile menu is ready
		document.addEventListener('mobilemenu-init-done', () => {
			window.bartender = new Bartender({
				debug: false,
				trapFocus: true,
				overlay: true,
			});

			window.addEventListener('bartender-open', (e) => {
				// When right bar is opened, focus to search query field
				if (e.detail.bar.position == 'right') {
					const searchQueryField = e.detail.bar.element.querySelector('[data-bartender-bar="right"] .searchForm__input')
					if (searchQueryField) searchQueryField.focus()
				}
			})
		});

		// Init mobile menu
		const mobileMenu = new MobileMenu();
		mobileMenu.init();

		// Initialize
		this.init(options);
	}

	/**
	 * Init the class by calling applicable init methods
	 *
	 * @param {Object} options Options for the class.
	 * @return {Object}
	 */
	init(options = {}) {

		// Merge user options to the defaults
		this.options = {
			responsiveTables: {
				selector: 'main table'
			},
			imageLinks: {
				parentSelector: 'main'
			},
			...options
		};

		// Call individual init methods
		this.initResponsiveTables();
		this.initSkipLinks();
		this.initImageLinks();
		this.initLiveSearch();
		this.initTopMenu();
		this.initCarousel();
		this.initBartenderLinks();

		// Dispatch custom event when init is done
		document.dispatchEvent(
			new CustomEvent('site-init-done', {
				bubbles: true,
				cancelable: true
			})
		);

		return this;
	}

	/**
	 * Initialize responsive tables
	 *
	 * Finds content tables and wraps them with div.table-wrapper.
	 */
	initResponsiveTables() {
		document.querySelectorAll(this.options.responsiveTables.selector).forEach(table => {
			if (!table.closest('.table-wrapper')) {
				const tableWrapper = document.createElement('div');
				tableWrapper.classList.add('table-wrapper');
				tableWrapper.classList.add('overflow-x-auto');
				table.parentNode.insertBefore(tableWrapper, table);
				tableWrapper.appendChild(table);
			}
		});
	}

	/**
	* Initialize skip links
	*
	* Finds skip links and enhances their behaviour for various screen readers and mobile devices.
	*/
	initSkipLinks() {
		const skipLinks = document.querySelectorAll('.skip-link:not([data-skip-link])');
		if (skipLinks.length) {
			const skipToBlur = event => {
				if (event.target.getAttribute('data-tabindex')) {
					event.target.removeAttribute('tabindex');
					event.target.removeAttribute('data-tabindex');
				}
			};
			skipLinks.forEach(skipLink => {
				skipLink.setAttribute('data-skip-link', true);
				skipLink.addEventListener('click', event => {
					const skipTo = document.getElementById(event.target.href.split('#')[1]);
					if (skipTo && skipTo.getAttribute('tabindex') != '-1') {
						event.preventDefault();
						skipTo.setAttribute('tabindex', '-1');
						skipTo.setAttribute('data-tabindex', true);
						skipTo.addEventListener('blur', skipToBlur);
						skipTo.addEventListener('focusout', skipToBlur);
						skipTo.focus();
					}
				});
			});
		}
	}

	/**
	* Init sliders
	*/
	initCarousel() {
		if (document.querySelector('.carousel')) {
			const slider = tns({
				container: '.carousel',
				items: 1,
				slideBy: 'page',
				mode: 'gallery',
				autoplayTimeout: 8000,
				autoplayButtonOutput: false,
				speed: 4000,
				autoplay: true,
				controls: false,
				nav: false,
			});
		}
	}

	/**
	 * Initialize image links
	 */
	initImageLinks() {

		// Parent of image links
		let parentNode = document.querySelector(this.options.imageLinks.parentSelector);
		if (!parentNode) return;

		// Add glightbox class to image links
		parentNode.querySelectorAll('a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".gif"]').forEach(link => {
			if (!link.classList.contains('glightbox')) {
				if (!link.getAttribute('data-title') && !link.getAttribute('data-glightbox')) {
					let figcaption = link.parentNode.querySelector('figcaption');
					if (figcaption) {
						let caption = figcaption ? figcaption.textContent : '';
						link.setAttribute('data-title', caption);
					}
				}
				link.classList.add('glightbox');
			}
		});

		// Initialize GLightbox
		if (parentNode.querySelector('.glightbox')) {
			GLightbox();
		}

	}

	/**
 	* Initialize live search forms
 	*/
	initLiveSearch() {
		const elements = document.querySelectorAll('.liveSearch')

		if (!elements.length) return

		elements.forEach(element => {
			const form = element.querySelector('.liveSearch__form')
			if (!form) return

			const actionUrl = form.getAttribute('action')
			if (!actionUrl) return

			const method = form.getAttribute('method')
			if (!method) return

			const appendToEl = element.querySelector('.liveSearch__results')
			if (!appendToEl) return

			// Prevent default submit
			element.addEventListener('submit', e => {
				e.preventDefault()
			})

			// Monitor search query changes
			let inputTimeout = null

			// Get form elements and watch for changes
			const inputs = form.querySelectorAll('input, textarea, select')

			inputs.forEach(item => {
				item.addEventListener('input', e => {
					clearTimeout(inputTimeout)

					inputTimeout = setTimeout(() => {
						const formData = Object.fromEntries(new FormData(form))
						const payload = {
							method: method,
							url: actionUrl,
							headers: { 'X-Requested-With': 'XMLHttpRequest' },
						}

						if (method == 'post') {
							payload.data = formData
						} else if (method == 'get') {
							payload.params = formData
						}

						element.classList.add('liveSearch--loading')

						axios(payload).then(response => {
							appendToEl.innerHTML = response.data
						}).catch(error => {
							console.error(error)
						}).then(() => (
							element.classList.remove('liveSearch--loading')
						))
					}, 200)
				})
			})

			// We might already have search query defined, so initially trigger search input
			// input.dispatchEvent(new Event('input', {
			// 	bubbles: true,
			// 	cancelable: false,
			// }))
		})
	}

	initTopMenu() {
		// Sticky nav
		const masthead = document.getElementById("site-masthead");

		function scrollDown() {
			if (window.pageYOffset >= masthead.offsetHeight) {
				masthead.classList.add("sticky-nav")
			} else {
				masthead.classList.remove("sticky-nav");
			}
		}

		window.addEventListener('scroll', event => {
			scrollDown();
		});
	}

	initBartenderLinks() {
		const bartenderLinks = document.querySelectorAll('a[data-bartender-toggle]')
		bartenderLinks.forEach(item => {
			item.addEventListener('click', e => {
				e.preventDefault();
			})
		})
	}
}

window._site = new Site();